import { create } from 'zustand';
import type { Channel, Sender, Recipient, MessageTemplate, SendMessageResult } from '@/types/messaging';

import { useModuleStore } from './module-store';
import { useAppStore } from './app-store';
import { useSettingsStore } from './settings-store';
import { useAuthStore } from './auth-store';
import { useConversationStore, getInboxOptOutListDocPath } from './conversation-store';
import { getSenderPermissionStandalone, senderPermissionAllowsSend } from '@/hooks/use-permissions';
import { uploadAttachment, deleteFile } from '@/lib/firebase/storage';
import { doc, getDocFromServer } from '@/lib/firebase/firestore';
import { db } from '@/config/firebase';
import { applyCountryCode } from '@/lib/utils/phone';
import { personalizeMessage, resolveTemplateVariables } from '@/lib/utils/message';
import {
  signatureWithOptOutFooter,
  optOutSenderMode,
  optOutRecipientPhones,
  optOutComposeKey,
  optOutListKeys,
  parseOptOutList,
  OPT_OUT_SKIPPED_TEXT,
  OPT_OUT_NOT_SENT_TEXT,
} from '@/lib/utils/opt-out';
import { enterBulkMode } from '@/lib/api/proxy';
import { runWithConcurrency } from '@/lib/utils/concurrency';
import type { ITPAModule } from '@/types/tpa';

/** KAN-155: says whether a recipient of this send opted out — when they did, or null. */
export type OptOutLookup = (recipient: Recipient) => { time: number } | null;
const NOBODY_OPTED_OUT: OptOutLookup = () => null;

/**
 * KAN-155: who among the recipients of this Compose send opted out of this sender.
 *
 * ONE read for the whole send, however many recipients: the sender's opt-out
 * list, which the server writes on every STOP / START. Each recipient is then
 * looked up in memory — under both shapes of their number where a country has
 * two. No read at all while opt-out is off or the sender is set to "Off". When
 * it can't be checked (no conversation id for this provider, or the read fails)
 * the send goes ahead as before.
 *
 * Always from the SERVER, never the app's cache-first getDoc: that one answers
 * from the local copy first, and a copy saved before a customer's STOP would
 * let their message through.
 *
 * Shared with the CSV bulk sender, which is a Compose send to many recipients.
 */
export async function loadOptOutLookup(
  tpaModule: ITPAModule,
  sender: Sender,
  recipients: Recipient[],
  channel: Channel,
): Promise<OptOutLookup> {
  try {
    const settings = useSettingsStore.getState().optOutSettings;
    if (optOutSenderMode(settings, sender.phoneNumber || sender.id, channel) === 'off') return NOBODY_OPTED_OUT;

    // The names a recipient may carry in the list: one per shape of their number.
    // Read from the inbox conversation id — or given by the provider's module
    // when that id can't tell them (MessageBird).
    const listKeysOf = (recipient: Recipient) => optOutRecipientPhones(recipient.phone).flatMap((phone) => {
      const convId = tpaModule.getInboxConversationId?.({ from: sender, to: [{ ...recipient, phone }], body: '', channel });
      const keys = tpaModule.getOptOutListKeys?.(sender, phone) ?? optOutListKeys(convId, channel);
      return keys ? [keys] : [];
    });

    // One sender, so one list — the first recipient that can be named tells which.
    let senderKey: string | null = null;
    for (const recipient of recipients) {
      senderKey = listKeysOf(recipient)[0]?.senderKey ?? null;
      if (senderKey) break;
    }
    const listPath = senderKey ? getInboxOptOutListDocPath(senderKey) : null;
    if (!listPath) return NOBODY_OPTED_OUT;

    const snapshot = await getDocFromServer(doc(db, listPath));
    const optedOut = parseOptOutList(snapshot.exists() ? snapshot.data() : null);
    if (optedOut.size === 0) return NOBODY_OPTED_OUT;

    return (recipient) => {
      try {
        for (const { customerKey } of listKeysOf(recipient)) {
          const time = optedOut.get(customerKey);
          if (time !== undefined) return { time };
        }
      } catch (err) {
        console.warn('[MessageStore] opt-out lookup failed — sending as before:', (err as Error)?.message || err);
      }
      return null;
    };
  } catch (err) {
    console.warn('[MessageStore] opt-out check failed — sending as before:', (err as Error)?.message || err);
    return NOBODY_OPTED_OUT;
  }
}

/**
 * KAN-155: the recipients in the Compose form who opted out of its sender — for
 * the preview of a send to several, which shows how many and lets the agent
 * choose. One read (loadOptOutLookup); nobody while opt-out is off or it can't
 * be checked. Numbers get the default country code first, as send() does.
 *
 * Returns their NUMBERS as the form holds them, not their ids: numbers typed or
 * pasted together share one id (`manual_<time>`), and an opt-out is about the
 * number anyway.
 */
export async function findOptedOutRecipientPhones(): Promise<string[]> {
  const { sender, channel, recipients } = useMessageStore.getState();
  const tpaModule = useModuleStore.getState().tpaModule;
  if (!sender || !tpaModule || recipients.length === 0) return [];
  const countryCode = useSettingsStore.getState().preferences.defaultCountryCode?.replace(/^\+/, '');
  const asSent = recipients.map((r) => ({ ...r, phone: applyCountryCode(r.phone, countryCode) }));
  const optOutOf = await loadOptOutLookup(tpaModule, sender, asSent, channel);
  return recipients.filter((_, i) => optOutOf(asSent[i])).map((r) => r.phone);
}

/** KAN-155: a Compose message held back because its one recipient opted out. */
export interface ComposeOptOutBlock {
  /** The sender + customer it was held back for — see optOutComposeKey(). */
  key: string;
  /** When the customer opted out, for the notice. */
  time?: number;
}

/** How many sendMessage CF calls run in parallel during a bulk send.
 * Trades latency for upstream pressure — TPA APIs (Twilio, WhatsApp Cloud,
 * etc.) tolerate well above 4 concurrent from a single account, and CFs
 * scale horizontally, so 4 keeps the user-visible speedup ~4× without
 * stressing the dialed-down account-level rate limits some TPAs apply.
 * Surface as a personal preference if customers want to dial up/down. */
const BULK_SEND_CONCURRENCY = 4;

/** How long a successful lastResult (the compose "Message sent successfully!"
 * banner) is kept before it is cleared. */
const SUCCESS_RESULT_MS = 5000;

interface AttachedFile {
  /** Local File object — present when user picked a new file via upload */
  file?: File;
  /** Local blob URL for <img> preview. May equal remoteUrl for remote attachments. */
  previewUrl: string;
  /**
   * Remote URL for an already-hosted file (e.g. when reverse-filling a saved
   * webhook template's MediaUrl). When present, no upload is needed — the
   * URL can be sent directly as the MediaUrl.
   */
  remoteUrl?: string;
  /** Display name for remote attachments (file?.name takes precedence) */
  name?: string;
}

/** Per-recipient result tracked during multi-recipient sends */
export interface RecipientSendResult {
  phone: string;
  name: string;
  body: string;
  success: boolean;
  error?: string;
  /** true if user cancelled before this recipient was sent */
  cancelled?: boolean;
  /** KAN-155: true if skipped because the customer opted out (replied STOP). */
  optedOut?: boolean;
  /** Provider message id, so delivery callbacks can find this row. */
  messageId?: string;
  /** Live delivery status ('sent' | 'delivered' | 'read' | 'failed: …').
   *  The provider ACCEPTS a message before delivering it, so success:true
   *  only means "accepted" — a failure can arrive seconds later and lands
   *  here via the realtime status stream. */
  deliveryStatus?: string;
}

interface MessageStore {
  sender: Sender | null;
  recipients: Recipient[];
  body: string;
  channel: Channel;
  selectedTemplate: MessageTemplate | null;
  templateVariables: Record<string, string>;
  attachedFile: AttachedFile | null;
  scheduledAt: Date | null;
  extraFields: Record<string, string>;
  sending: boolean;
  sendError: string | null;
  lastResult: SendMessageResult | null;
  /** Live progress during multi-recipient sends */
  sendProgress: { current: number; total: number } | null;
  /** Per-recipient results accumulated during send */
  sendResults: RecipientSendResult[];
  /** Pause/cancel control flags for multi-recipient sends */
  sendPaused: boolean;
  sendCancelled: boolean;
  /** Snapshot of recipients at time of send (used by progress dialog) */
  sendRecipients: Recipient[];
  /** KAN-155: set when the last send to ONE recipient was held back because
   *  that customer opted out. The Compose notice offers "Send anyway" for it. */
  optOutBlock: ComposeOptOutBlock | null;

  setSender(sender: Sender): void;
  addRecipient(r: Recipient): void;
  addRecipients(recipients: Recipient[]): void;
  setRecipients(recipients: Recipient[]): void;
  removeRecipient(id: string): void;
  clearRecipients(): void;
  /** True after user manually clears recipients — prevents auto-fill from re-adding */
  recipientsCleared: boolean;
  /**
   * True while the phone-field pill filter is narrowing CRM recipients —
   * prevents auto-fill from re-adding recipients the filter removed
   * (filtering down to zero would otherwise trigger a silent refill,
   * making the pills look like they do nothing).
   */
  recipientsPillFiltered: boolean;
  setRecipientsPillFiltered(active: boolean): void;
  setBody(body: string): void;
  setChannel(channel: Channel): void;
  setTemplate(t: MessageTemplate | null): void;
  setTemplateVariable(key: string, value: string): void;
  setAttachedFile(file: AttachedFile | null): void;
  setScheduledAt(date: Date | null): void;
  setExtraField(key: string, value: string): void;
  /** `optOutSendAnyway` (KAN-155): the agent confirmed "Send anyway" on the
   *  held-back message — send it without checking the opt-out again.
   *  `optOutSkipPhones` (KAN-155): the preview of a send to several recipients
   *  already checked the opt-out list — skip exactly these numbers (none =
   *  the agent ticked "Send to them anyway"). The list is not read again. */
  send(options?: { dryRun?: boolean; optOutSendAnyway?: boolean; optOutSkipPhones?: string[] }): Promise<SendMessageResult>;
  pauseSend(): void;
  resumeSend(): void;
  cancelSend(): void;
  clearSendResults(): void;
  /** Realtime delivery callbacks update the Send Complete rows through this. */
  applyDeliveryStatus(messageId: string, status: string): void;
  reset(): void;
  /**
   * Bumped by every reset(). Components that keep compose state of their own
   * (loaded CSV sheets, phone-field pills, typed text) watch it to clear that
   * state too — the store can't reach it. (KAN-218)
   */
  resetCount: number;
}

/**
 * The bulk-send loop rebuilds `sendResults` from its private `accumulated`
 * array on every recipient. A realtime delivery callback (applyDeliveryStatus)
 * meanwhile flips an already-completed row to failed by writing the STORE —
 * which the loop's next `set([...accumulated])` would overwrite, snapping the
 * row back to success. This preserves any flip already applied to the store
 * (matched by messageId) when the loop rewrites the list.
 *
 * Inert for every app that doesn't run applyDeliveryStatus: with no flipped
 * rows in the store, it returns a plain copy of `accumulated`, exactly as the
 * old `[...accumulated]` did.
 */
function mergeDeliveryFlips(
  base: RecipientSendResult[],
  storeRows: RecipientSendResult[]
): RecipientSendResult[] {
  try {
    if (!Array.isArray(storeRows) || storeRows.length === 0) return [...base];
    const flips = new Map<string, RecipientSendResult>();
    for (const r of storeRows) {
      if (r && r.messageId && r.deliveryStatus !== undefined) flips.set(r.messageId, r);
    }
    if (flips.size === 0) return [...base];
    return base.map((r) => {
      const flip = r && r.messageId ? flips.get(r.messageId) : undefined;
      if (!flip) return r;
      return {
        ...r,
        deliveryStatus: flip.deliveryStatus,
        ...(flip.success === false ? { success: false, error: flip.error } : {}),
      };
    });
  } catch {
    // A merge bug must never break the send loop — fall back to the plain copy,
    // i.e. exactly the old `[...accumulated]` behaviour.
    return [...base];
  }
}

export const useMessageStore = create<MessageStore>((set, get) => ({
  sender: null,
  recipients: [],
  recipientsCleared: false,
  recipientsPillFiltered: false,
  body: '',
  channel: 'SMS',
  selectedTemplate: null,
  templateVariables: {},
  attachedFile: null,
  scheduledAt: null,
  extraFields: {},
  sending: false,
  sendError: null,
  lastResult: null,
  sendProgress: null,
  sendResults: [],
  sendPaused: false,
  sendCancelled: false,
  sendRecipients: [],
  optOutBlock: null,
  resetCount: 0,

  setSender: (sender) => set({ sender, channel: sender.channel }),

  addRecipient: (r) => {
    const existing = get().recipients;
    // Avoid duplicates by phone
    if (existing.some((e) => e.phone === r.phone)) return;
    set({ recipients: [...existing, r] });
  },

  addRecipients: (recipients) => {
    const existing = get().recipients;
    const existingPhones = new Set(existing.map((r) => r.phone));
    const newRecipients = recipients.filter((r) => !existingPhones.has(r.phone));
    set({ recipients: [...existing, ...newRecipients] });
  },

  setRecipients: (recipients) => set({ recipients }),

  removeRecipient: (id) =>
    set({ recipients: get().recipients.filter((r) => r.id !== id) }),

  clearRecipients: () => set({ recipients: [], recipientsCleared: true }),

  setRecipientsPillFiltered: (active) => set({ recipientsPillFiltered: active }),

  setBody: (body) => set({ body }),

  setChannel: (channel) => set({ channel }),

  setTemplate: (t) => {
    if (t) {
      set({
        selectedTemplate: t,
        body: t.body,
        templateVariables: {},
      });
    } else {
      set({ selectedTemplate: null, templateVariables: {}, body: '' });
    }
  },

  setTemplateVariable: (key, value) =>
    set({
      templateVariables: { ...get().templateVariables, [key]: value },
    }),

  setAttachedFile: (file) => {
    const prev = get().attachedFile;
    if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
    set({ attachedFile: file });
  },

  setScheduledAt: (scheduledAt) => set({ scheduledAt }),

  setExtraField: (key, value) =>
    set({ extraFields: { ...get().extraFields, [key]: value } }),

  send: async (options) => {
    const dryRun = options?.dryRun ?? false;
    const { sender, body, channel, selectedTemplate, templateVariables, scheduledAt, extraFields } = get();
    const tpaModule = useModuleStore.getState().tpaModule;
    const prefs = useSettingsStore.getState().preferences;
    // KAN-155: the opt-out footer (Settings › Customer Opt-out) rides along with
    // the signature, so it lands exactly where the signature does — the same
    // for one recipient or many. The dry run below keeps its own signature-only
    // copy — workflow messages get their footer from the server at send time.
    // Saved templates are plain text (footer added); approved ones are not.
    const sig = signatureWithOptOutFooter(
      prefs.messageSignature,
      useSettingsStore.getState().optOutSettings,
      'compose',
      {
        body,
        channel,
        isApprovedTemplate: !!selectedTemplate && !selectedTemplate.id.startsWith('ua_msg_template_item_'),
      },
    );
    const countryCode = prefs.defaultCountryCode?.replace(/^\+/, '');
    const authUser = useAuthStore.getState().user;
    const currentUser = authUser ? { displayName: authUser.displayName, email: authUser.email, phoneNumber: authUser.phoneNumber } : undefined;

    // Apply default country code to all recipients before sending
    const recipients = get().recipients.map((r) => ({
      ...r,
      phone: applyCountryCode(r.phone, countryCode),
    }));

    if (!tpaModule) {
      const result: SendMessageResult = {
        success: false,
        error: 'No messaging provider loaded',
      };
      set({ lastResult: result });
      return result;
    }

    if (!sender) {
      const result: SendMessageResult = {
        success: false,
        error: 'No sender selected',
      };
      set({ lastResult: result });
      return result;
    }

    if (recipients.length === 0) {
      const result: SendMessageResult = {
        success: false,
        error: 'No recipients selected',
      };
      set({ lastResult: result });
      return result;
    }

    // KAN-14: permission gate for every send routed through this store —
    // compose, bulk-from-CSV and scheduled sends. It is NOT a universal safety
    // net: the chat composer (chat-input), Retry (chat-thread) and the
    // bulk-sender component call tpaModule.sendMessage() directly and carry
    // their own gates, so a new surface that skips this store must gate itself.
    // Internal notes never reach this store, so no exemption is needed.
    // Dry runs don't deliver.
    if (!dryRun && !senderPermissionAllowsSend(getSenderPermissionStandalone(sender.id))) {
      const result: SendMessageResult = {
        success: false,
        error: "You don't have permission to send from this sender",
      };
      // sendError too, not just lastResult — the inbox panel's scheduled-send
      // UI surfaces sendError, so without it a blocked send fails silently.
      set({ lastResult: result, sendError: result.error ?? null });
      return result;
    }

    // Dry run: intercept the TPA's API call to capture the payload without sending
    if (dryRun) {
      try {
        const recipient = recipients[0];
        const sig = prefs.messageSignature?.trim();
        const finalBody = sig ? `${body}\n${sig}` : body;
        const resolvedVars = selectedTemplate && templateVariables
          ? resolveTemplateVariables(templateVariables, {
              name: recipient?.name || undefined,
              phone: recipient?.phone || undefined,
              email: recipient?.email,
              properties: recipient?.properties,
            })
          : templateVariables;

        // If a file is attached, upload it so the captured payload includes a real
        // mediaUrl (matches legacy app behavior — workflow code/webhook payloads
        // contain the actual MediaUrl field).
        let dryRunMediaUrl: string | undefined;
        const { attachedFile } = get();
        if (attachedFile?.remoteUrl) {
          // Already-hosted file (e.g. reverse-filled from saved webhook) — no upload needed
          dryRunMediaUrl = attachedFile.remoteUrl;
        } else if (attachedFile?.file) {
          try {
            const uploaded = await uploadAttachment(attachedFile.file);
            dryRunMediaUrl = uploaded.url;
          } catch (err) {
            console.warn('[MessageStore] dryRun media upload failed', err);
          }
        }
        // Derive media type/filename so the captured (workflow) payload uses the
        // right WhatsApp media type. dryRun stays URL-based — modules must NOT
        // pre-upload to the provider during a dry run (workflow webhooks fetch
        // the media by URL server-side).
        let dryRunMediaType: 'image' | 'video' | 'audio' | 'document' | undefined;
        const dryRunMime = attachedFile?.file?.type ?? '';
        if (dryRunMime.startsWith('image/')) dryRunMediaType = 'image';
        else if (dryRunMime.startsWith('video/')) dryRunMediaType = 'video';
        else if (dryRunMime.startsWith('audio/')) dryRunMediaType = 'audio';
        else if (dryRunMediaUrl) dryRunMediaType = 'document';

        // Temporarily replace the module's api with a proxy that captures the payload
        // and throws to abort before any post-send side effects (Firestore writes etc.)
        let capturedPayload: Record<string, unknown> | null = null;
        const DRY_RUN_ABORT = Symbol('DRY_RUN_ABORT');
        const realApi = (tpaModule as unknown as { api: unknown }).api;
        const proxyApi = new Proxy(realApi as object, {
          get(_target, _prop) {
            return (...args: unknown[]) => {
              // Capture the message payload (typically the last object argument)
              for (let i = args.length - 1; i >= 0; i--) {
                if (args[i] && typeof args[i] === 'object' && !Array.isArray(args[i])) {
                  capturedPayload = args[i] as Record<string, unknown>;
                  break;
                }
              }
              // Throw to abort execution before any post-send side effects
              throw DRY_RUN_ABORT;
            };
          },
        });
        (tpaModule as unknown as { api: unknown }).api = proxyApi;

        try {
          await tpaModule.sendMessage({
            from: sender,
            to: [recipient],
            body: finalBody,
            channel,
            templateId: selectedTemplate?.id,
            selectedTemplate: selectedTemplate ?? undefined,
            templateVariables: resolvedVars,
            extraFields: Object.keys(extraFields).length > 0 ? extraFields : undefined,
            mediaUrl: dryRunMediaUrl,
            mediaType: dryRunMediaType,
            mediaFilename: attachedFile?.file?.name,
            dryRun: true,
          });
        } catch (err) {
          // Only swallow our own abort signal; rethrow real errors
          if (err !== DRY_RUN_ABORT) throw err;
        } finally {
          (tpaModule as unknown as { api: unknown }).api = realApi;
        }

        if (capturedPayload) {
          return { success: true, dryRunPayload: capturedPayload };
        }
        return { success: false, error: 'Could not capture payload' };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Failed to build payload' };
      }
    }

    // KAN-155: "Send anyway" counts only for the customer the agent was asked
    // about — the same sender and recipient the message was held back for.
    const optOutKey = optOutComposeKey(sender, channel, get().recipients);
    const optOutConfirmed = options?.optOutSendAnyway === true
      && optOutKey !== null
      && get().optOutBlock?.key === optOutKey;

    // Reset control flags
    set({ sending: true, sendError: null, sendPaused: false, sendCancelled: false, optOutBlock: null });

    try {
      // KAN-155: who of the recipients opted out of this sender — ONE read for
      // the whole send (the sender's opt-out list), none after "Send anyway" or
      // when the preview already checked (optOutSkipPhones — numbers as the form
      // holds them, so they get the country code here like the recipients did).
      const optOutSkipPhones = options?.optOutSkipPhones
        ? new Set(options.optOutSkipPhones.map((phone) => applyCountryCode(phone, countryCode)))
        : null;
      const optOutOf: OptOutLookup = optOutConfirmed
        ? NOBODY_OPTED_OUT
        : optOutSkipPhones
          ? (recipient) => (optOutSkipPhones.has(recipient.phone) ? { time: 0 } : null)
          : await loadOptOutLookup(tpaModule, sender, recipients, channel);

      // ONE recipient who opted out — the message is held back (nothing is sent
      // or uploaded) and Compose asks the agent: "Send anyway?". Of several
      // recipients, the ones who opted out are skipped in the loop below.
      const heldBackFor = optOutKey !== null ? optOutOf(recipients[0]) : null;
      if (optOutKey !== null && heldBackFor) {
        const result: SendMessageResult = { success: false, error: OPT_OUT_NOT_SENT_TEXT };
        set({
          sending: false,
          sendProgress: null,
          lastResult: result,
          sendPaused: false,
          sendCancelled: false,
          optOutBlock: { key: optOutKey, time: heldBackFor.time },
        });
        return result;
      }

      const { attachedFile } = get();
      const hasPlaceholders = /\{\{[\w-]+\}\}/.test(body);
      const hasProperties = recipients.some((r) => r.properties && Object.keys(r.properties).length > 0);

      // Upload file to Firebase Storage temp path to get a public URL (Twilio needs publicly accessible URLs)
      let mediaUrl: string | undefined;
      let uploadedPath: string | undefined;
      let mediaType: 'image' | 'video' | 'audio' | 'document' | undefined;
      let mediaFilename: string | undefined;
      console.log('[MessageStore] attachedFile state:', attachedFile ? { hasFile: !!attachedFile.file, fileName: attachedFile.file?.name, fileSize: attachedFile.file?.size, previewUrl: attachedFile.previewUrl ? 'set' : 'unset' } : 'NULL');
      if (attachedFile?.remoteUrl && !attachedFile.file) {
        // Already-hosted file — skip upload, use remote URL directly
        mediaUrl = attachedFile.remoteUrl;
        console.log('[MessageStore] Using remote attachedFile.remoteUrl directly:', mediaUrl?.substring(0, 80));
      } else if (attachedFile?.file) {
        const uploaded = await uploadAttachment(attachedFile.file);
        mediaUrl = uploaded.url;
        uploadedPath = uploaded.path;
        // Derive WhatsApp media type from MIME type so images/videos aren't sent as documents
        const mime = attachedFile.file.type || '';
        if (mime.startsWith('image/')) mediaType = 'image';
        else if (mime.startsWith('video/')) mediaType = 'video';
        else if (mime.startsWith('audio/')) mediaType = 'audio';
        else mediaType = 'document';
        mediaFilename = attachedFile.file.name || undefined;
        console.log('[MessageStore] File uploaded to Firebase:', mediaUrl?.substring(0, 80), 'mediaType:', mediaType);
      } else {
        console.log('[MessageStore] No attachedFile.file — skipping upload. mediaUrl will be undefined.');
      }

      let result: SendMessageResult;
      const isMulti = recipients.length > 1;

      // Debug: log all recipient phones to trace multi-send issues
      console.log('[MessageStore] Send recipients:', recipients.map((r, i) => `[${i}] id=${r.id} phone=${r.phone}`));

      const accumulated: RecipientSendResult[] = [];
      /** The actual message body sent (with placeholders resolved + signature appended). */
      let resolvedBody = body;

      if (isMulti) {
        // ── Multi-recipient: send one-by-one with progress tracking ──
        const total = recipients.length;
        set({
          sendProgress: { current: 0, total },
          sendResults: [],
          sendRecipients: recipients,
        });

        let allSuccess = true;
        let completedCount = 0;

        // Same rationale as `bulk-sender.tsx` — the user explicitly chose
        // to send to N recipients, so the per-URL 200/min loop guard
        // shouldn't apply. `releaseBulkMode()` is called after the loop
        // (and after cancellation/error paths) below.
        const releaseBulkMode = enterBulkMode();
        try {
        // Parallelize sends with a small concurrency window. Each worker
        // pulls the next recipient off the shared cursor, so cancellation
        // mid-batch only stops NEW work — in-flight sends complete and
        // record their result. Out-of-order completion means
        // `sendResults` reflects completion order, not input order; the UI
        // doesn't depend on alignment with `sendRecipients[index]`.
        await runWithConcurrency(recipients, BULK_SEND_CONCURRENCY, async (recipient, i) => {
          // Bail early if cancelled — record a cancelled placeholder so
          // the totals (sent / failed / cancelled) stay correct.
          const recordCancelled = () => {
            accumulated.push({
              phone: recipient.phone,
              name: recipient.name || recipient.phone,
              body: '',
              success: false,
              cancelled: true,
            });
            completedCount++;
            set((state) => ({
              sendProgress: { current: completedCount, total },
              sendResults: mergeDeliveryFlips(accumulated, state.sendResults),
            }));
          };

          if (get().sendCancelled) {
            recordCancelled();
            return;
          }

          // Pause: spin until the user resumes (or cancels).
          while (get().sendPaused && !get().sendCancelled) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
          if (get().sendCancelled) {
            recordCancelled();
            return;
          }

          // KAN-155: skip a customer who opted out. Not a failure — the rest of
          // the batch goes out and this row says why it did not. Looked up in
          // the list read above, so it costs no read of its own.
          if (optOutOf(recipient)) {
            accumulated.push({
              phone: recipient.phone,
              name: recipient.name || recipient.phone,
              body: '',
              success: false,
              optedOut: true,
              error: OPT_OUT_SKIPPED_TEXT,
            });
            completedCount++;
            set((state) => ({
              sendProgress: { current: completedCount, total },
              sendResults: mergeDeliveryFlips(accumulated, state.sendResults),
            }));
            return;
          }

          console.log(`[MessageStore] Sending ${i + 1}/${total}: phone=${recipient.phone} id=${recipient.id}`);
          const messageBody = personalizeMessage({
            body,
            recipient,
            signature: sig,
            hasPlaceholders,
            hasProperties,
            isTemplate: !!selectedTemplate,
            currentUser,
          });

          // Resolve {{property}} entity placeholders in template variable values
          // per-recipient (e.g. {{First_Name}} → "John" for this specific recipient)
          const resolvedVars = selectedTemplate && templateVariables
            ? resolveTemplateVariables(templateVariables, {
                name: recipient.name || undefined,
                phone: recipient.phone || undefined,
                email: recipient.email,
                properties: recipient.properties,
              })
            : templateVariables;

          let sendResult: SendMessageResult;
          try {
            sendResult = await tpaModule.sendMessage({
              from: sender,
              to: [recipient],
              body: messageBody,
              channel,
              templateId: selectedTemplate?.id,
              selectedTemplate: selectedTemplate ?? undefined,
              templateVariables: selectedTemplate ? resolvedVars : undefined,
              mediaUrl,
              mediaType,
              mediaFilename,
              scheduledAt: scheduledAt ?? undefined,
              extraFields: Object.keys(extraFields).length > 0 ? extraFields : undefined,
            });
          } catch (err) {
            sendResult = {
              success: false,
              error: err instanceof Error ? err.message : 'Send failed',
            };
          }

          if (!sendResult.success) allSuccess = false;

          // Log sent message to CRM history (non-blocking)
          // Uses tpaModule.logSentMessageToHistory which resolves {{1}}, {{2}} etc.
          if (sendResult.success) {
            const sendPayloadForLogging = {
              from: sender,
              to: [recipient],
              body: messageBody,
              channel,
              templateId: selectedTemplate?.id,
              selectedTemplate: selectedTemplate ?? undefined,
              templateVariables: selectedTemplate ? resolvedVars : undefined,
              mediaUrl,
              mediaType,
              mediaFilename,
            };
            tpaModule.logSentMessageToHistory?.(sendPayloadForLogging, sendResult).catch(() => {});
            // Seed UA Inbox with the outgoing message + author info.
            // No-op for MessageBird (different conv ID format) and
            // WhatsApp (already writes inline from executeSend).
            tpaModule.logOutgoingMessageToInbox?.(sendPayloadForLogging, sendResult).catch(() => {});
            tpaModule.updateConversationLinkedRecord?.(sendPayloadForLogging, sendResult).catch(() => {});
          }

          // Let the SaaS record the immediate send outcome (Google Sheets writes
          // the message id + first status straight into the row). Runs for
          // failures too, so a failed row is not left blank.
          useModuleStore.getState().saasModule?.recordSendOutcome?.({
            messageId: sendResult.messageId,
            phone: recipient.phone,
            success: sendResult.success,
            providerStatus: sendResult.providerStatus,
            error: sendResult.error,
          }).catch(() => {});

          accumulated.push({
            phone: recipient.phone,
            name: recipient.name || recipient.phone,
            body: messageBody.replace(/\n.*$/, ''), // Strip signature for preview
            success: sendResult.success,
            error: sendResult.error,
            messageId: sendResult.messageId,
          });

          completedCount++;
          set((state) => ({
            sendProgress: { current: completedCount, total },
            sendResults: mergeDeliveryFlips(accumulated, state.sendResults),
          }));

          // ── Per-worker delay between sends (reuses bulkSendDelay) ──
          // Applied within each worker so a configured delay still throttles
          // throughput predictably. Effective rate ≈ concurrency / delay.
          const { bulkSendDelay = 0 } = useSettingsStore.getState().preferences;
          if (bulkSendDelay > 0 && completedCount < total) {
            await new Promise((resolve) => setTimeout(resolve, bulkSendDelay));
          }
        });
        } finally {
          releaseBulkMode();
        }

        const cancelledCount = accumulated.filter((r) => r.cancelled).length;

        // A list-view "select all" loads one page per sending session. Record
        // that this page went out so reopening the same list offers the next
        // one instead of starting from page 1 again. Only on a real send —
        // a fully cancelled batch leaves the position untouched.
        if (accumulated.some((r) => r.success)) {
          useModuleStore.getState().saasModule?.markListViewPageSent?.().catch(() => {});
        }

        result = {
          success: allSuccess && cancelledCount === 0,
          error: cancelledCount > 0
            ? `${cancelledCount} message${cancelledCount > 1 ? 's' : ''} cancelled`
            : allSuccess
              ? undefined
              : 'Some messages failed to send',
        };
      } else if (hasPlaceholders && hasProperties && !selectedTemplate) {
        // ── Single recipient with placeholders ──
        const recipient = recipients[0];
        const messageBody = personalizeMessage({
          body,
          recipient,
          signature: sig,
          hasPlaceholders,
          hasProperties,
          isTemplate: false,
          currentUser,
        });
        resolvedBody = messageBody;

        result = await tpaModule.sendMessage({
          from: sender,
          to: [recipient],
          body: messageBody,
          channel,
          mediaUrl,
          scheduledAt: scheduledAt ?? undefined,
          extraFields: Object.keys(extraFields).length > 0 ? extraFields : undefined,
        });
      } else {
        // ── Single recipient or batch (no placeholders) ──
        const finalBody = sig ? `${body}\n${sig}` : body;
        resolvedBody = finalBody;

        // Resolve {{property}} entity placeholders inside template variable values
        // per-recipient (e.g. {{First_Name}} → "John") just like the multi-recipient path
        const recipient = recipients[0];
        const resolvedVars = selectedTemplate && templateVariables
          ? resolveTemplateVariables(templateVariables, {
              name: recipient?.name || undefined,
              phone: recipient?.phone || undefined,
              email: recipient?.email,
              properties: recipient?.properties,
            })
          : templateVariables;

        result = await tpaModule.sendMessage({
          from: sender,
          to: recipients,
          body: finalBody,
          channel,
          templateId: selectedTemplate?.id,
          selectedTemplate: selectedTemplate ?? undefined,
          templateVariables: resolvedVars,
          mediaUrl,
          scheduledAt: scheduledAt ?? undefined,
          extraFields: Object.keys(extraFields).length > 0 ? extraFields : undefined,
        });
      }

      // CRM history logging is handled by tpaModule.logSentMessageToHistory —
      // for multi-recipient sends it's called per-recipient inside the loop above
      // (line ~392), and for single-recipient sends it's called below (line ~578).
      // Both delegate to saasModule.addSentMessageToHistory internally.
      // A second direct call to saasModule here would create duplicate notes/
      // timeline entries in all CRMs (Pipedrive, HubSpot, Zoho, etc.).

      // Clean up temp file from storage after Twilio has fetched it
      if (uploadedPath && useAppStore.getState().appCodeTPA === 'twilio') {
        deleteFile(uploadedPath).catch(() => {});
      }

      // Store sent-message meta in Firestore (source/author) so shared users
      // see proper author attribution on the chat view. No-op for WhatsApp.
      // Conv ID comes from the TPA so each provider's inbox format is
      // honoured — Twilio prefixes WhatsApp/SMS addresses, MessageBird
      // returns null (skipping the write since its conv IDs are upstream
      // UUIDs the client can't reconstruct).
      if (!isMulti && result.success && result.messageId && recipients.length > 0) {
        const recipient = recipients[0];
        const contactPhoneDigits = (recipient?.phone || '').replace(/\D/g, '');
        const convIdForMeta = tpaModule.getInboxConversationId?.({
          from: sender,
          to: [recipient],
          body: resolvedBody || body,
          channel,
          mediaUrl,
          mediaType,
          mediaFilename,
        });
        if (convIdForMeta && contactPhoneDigits) {
          useConversationStore.getState().storeSentMessageMeta(
            convIdForMeta,
            contactPhoneDigits,
            result.messageId,
            resolvedBody || body
          ).catch(() => {});
        }
      }

      // Log single-recipient sends to CRM history (non-blocking).
      // Multi-recipient sends are logged per-recipient inside the loop above.
      // Uses tpaModule.logSentMessageToHistory which resolves template variables.
      if (!isMulti && result.success && recipients.length > 0) {
        const recipient = recipients[0];
        // Resolve template variable entity placeholders for this recipient
        const historyVars = selectedTemplate && templateVariables
          ? resolveTemplateVariables(templateVariables, {
              name: recipient?.name || undefined,
              phone: recipient?.phone || undefined,
              email: recipient?.email,
              properties: recipient?.properties,
            })
          : templateVariables;

        const historyBody = (hasPlaceholders && hasProperties && !selectedTemplate)
          ? personalizeMessage({ body, recipient, signature: sig, hasPlaceholders, hasProperties, isTemplate: false, currentUser })
          : (sig ? `${body}\n${sig}` : body);

        const singleSendPayloadForLogging = {
          from: sender,
          to: [recipient],
          body: historyBody,
          channel,
          templateId: selectedTemplate?.id,
          selectedTemplate: selectedTemplate ?? undefined,
          templateVariables: selectedTemplate ? historyVars : undefined,
          mediaUrl,
        };
        tpaModule.logSentMessageToHistory?.(singleSendPayloadForLogging, result).catch(() => {});
        // Seed UA Inbox — see multi-recipient block above for rationale.
        tpaModule.logOutgoingMessageToInbox?.(singleSendPayloadForLogging, result).catch(() => {});
        useModuleStore.getState().saasModule?.recordSendOutcome?.({
          messageId: result.messageId,
          phone: recipients[0]?.phone || '',
          success: result.success,
          providerStatus: result.providerStatus,
          error: result.error,
        }).catch(() => {});
        tpaModule.updateConversationLinkedRecord?.(singleSendPayloadForLogging, result).catch(() => {});
      }

      set({ sending: false, sendProgress: null, lastResult: result, sendPaused: false, sendCancelled: false });

      // KAN-190: the success banner used to stay until the next send. Clear it
      // here, not in SendButton, so leaving Compose and coming back can't show
      // an old success again. Skip if a newer send has replaced it.
      if (result.success) {
        setTimeout(() => {
          if (get().lastResult === result) set({ lastResult: null });
        }, SUCCESS_RESULT_MS);
      }

      if (result.success || isMulti) {
        // Reset form after send (for multi, reset even if some failed)
        if (get().attachedFile?.previewUrl) URL.revokeObjectURL(get().attachedFile!.previewUrl);
        const saasCode = useAppStore.getState().appCodeSAAS || '';
        const keepRecipients = saasCode === 'spreadsheet' || saasCode === 'googlesheets';
        set({
          // Clearing recipients must clear the pill-filter flag too. Otherwise
          // the flag stays true after a send, auto-fill stays blocked, and the
          // To field is empty until a full page reload.
          ...(keepRecipients ? {} : { recipients: [], recipientsPillFiltered: false }),
          body: '',
          selectedTemplate: null,
          templateVariables: {},
          attachedFile: null,
          scheduledAt: null,
          extraFields: {},
        });
      } else {
        set({ sendError: result.error || 'Send failed' });
      }

      return result;
    } catch (error) {
      const result: SendMessageResult = {
        success: false,
        error: error instanceof Error ? error.message : 'Send failed',
      };
      set({ sending: false, sendProgress: null, sendError: result.error!, lastResult: result, sendPaused: false, sendCancelled: false });
      return result;
    }
  },

  pauseSend: () => set({ sendPaused: true }),
  resumeSend: () => set({ sendPaused: false }),
  cancelSend: () => set({ sendCancelled: true, sendPaused: false }),

  clearSendResults: () => set({ sendResults: [], sendProgress: null, sendRecipients: [], sendPaused: false, sendCancelled: false }),

  applyDeliveryStatus: (messageId, status) => {
    if (!messageId || !status) return;
    const rows = get().sendResults;
    if (!rows.length) return;
    const idx = rows.findIndex((r) => r.messageId === messageId);
    if (idx === -1) return;
    const failed = /^failed/i.test(status);
    const next = [...rows];
    next[idx] = {
      ...next[idx],
      deliveryStatus: status,
      // The provider accepted the send, then reported failure — flip the row
      // so the Send Complete panel stops claiming success.
      ...(failed ? { success: false, error: status } : {}),
    };
    set({ sendResults: next });
  },

  reset: () => {
    const prev = get().attachedFile;
    if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
    set({
      sender: null,
      recipients: [],
      recipientsCleared: false,
      recipientsPillFiltered: false,
      body: '',
      channel: 'SMS',
      selectedTemplate: null,
      templateVariables: {},
      attachedFile: null,
      scheduledAt: null,
      extraFields: {},
      sending: false,
      sendError: null,
      lastResult: null,
      sendProgress: null,
      sendResults: [],
      sendPaused: false,
      sendCancelled: false,
      sendRecipients: [],
      optOutBlock: null,
      resetCount: get().resetCount + 1,
    });
  },
}));
